import {
  type MonthlyActivityComparison,
  type MonthlyActivityDay,
  PracticeTimeChangeType,
  ReportSkillTrend,
} from "@/generated/graphql";
import { format, formatMonthYear, useLocale, useT, weekdayNarrow } from "@/i18n";
import { useMemo, useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { DayActivityDetail } from "./DayActivityDetail";
import { MonthYearPicker } from "./MonthYearPicker";
import { TREND_STYLES, formatPracticeTime } from "./reportTokens";

interface MonthlyActivityCardProps {
  studentId: string;
  timezone: string;
  year: number;
  month: number;
  currentYear: number;
  currentMonth: number;
  totalPracticeTimeMs: number;
  /** Null unless the real current month is on screen — the chip is simply omitted then. */
  practiceTimeComparison: MonthlyActivityComparison | null | undefined;
  days: MonthlyActivityDay[];
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectMonth: (year: number, month: number) => void;
  canGoToNextMonth: boolean;
}

/** Sunday-first, as the calendar grid is. Narrow initials repeat within a week, so these are the
 * row's keys. */
const WEEKDAY_IDS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

// PracticeTimeChangeType has no "steady", so it borrows the shared 3-state trend palette rather
// than inventing a second one.
const CHANGE_TYPE_TO_TREND: Record<PracticeTimeChangeType, ReportSkillTrend> = {
  [PracticeTimeChangeType.Increase]: ReportSkillTrend.Improving,
  [PracticeTimeChangeType.Decrease]: ReportSkillTrend.Declining,
  [PracticeTimeChangeType.NoChange]: ReportSkillTrend.Steady,
};

/** "↑ 4 min/day" — sitting beside the month's practice time is what says what it compares to. */
function ComparisonChip({ comparison }: { comparison: MonthlyActivityComparison }) {
  const t = useT();
  const trend = TREND_STYLES[CHANGE_TYPE_TO_TREND[comparison.changeType]];
  const TrendIcon = trend.icon;

  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold"
      style={{ backgroundColor: trend.background, color: trend.color }}
    >
      <TrendIcon className="h-3 w-3" />
      {format(t["report.activity.minPerDay"], {
        count: Math.abs(comparison.deltaMinutesPerDay),
      })}
    </span>
  );
}

/** The month's practice time, and which days it landed on. */
export function MonthlyActivityCard({
  studentId,
  timezone,
  year,
  month,
  currentYear,
  currentMonth,
  totalPracticeTimeMs,
  practiceTimeComparison,
  days,
  onPrevMonth,
  onNextMonth,
  onSelectMonth,
  canGoToNextMonth,
}: MonthlyActivityCardProps) {
  const t = useT();
  const locale = useLocale();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openedDate, setOpenedDate] = useState<string | null>(null);

  const weekdays = useMemo(
    () => WEEKDAY_IDS.map((id, index) => ({ id, initial: weekdayNarrow(locale, index) })),
    [locale]
  );

  // A selected day belongs to the month it was clicked in, so paging away drops the selection.
  const selectedDay = days.find((day) => day.date === openedDate) ?? null;

  const monthLabel = formatMonthYear(locale, new Date(year, month - 1, 1));
  // Leading blanks so day 1 lands under its real weekday (0 = Sunday, as the initials start).
  const leadingBlanks = new Date(year, month - 1, 1).getDay();

  return (
    <div className="app-card px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <p className="app-value text-app-text">
          <span className="font-kumbh text-2xl font-extrabold text-app-ink">
            {formatPracticeTime(t, totalPracticeTimeMs)}
          </span>{" "}
          {t["report.activity.thisMonth"]}
        </p>
        {practiceTimeComparison && <ComparisonChip comparison={practiceTimeComparison} />}
      </div>

      <div className="relative mt-3 flex items-center justify-between">
        <button
          type="button"
          onClick={onPrevMonth}
          aria-label={t["report.activity.previousMonth"]}
          className="flex h-8 w-8 items-center justify-center rounded-full text-app-muted hover:bg-gray-100"
        >
          <HiChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen((open) => !open)}
          className="rounded-full px-3 py-1 text-sm font-bold text-app-muted hover:bg-gray-100"
        >
          {monthLabel}
        </button>
        <button
          type="button"
          onClick={onNextMonth}
          disabled={!canGoToNextMonth}
          aria-label={t["report.activity.nextMonth"]}
          className="flex h-8 w-8 items-center justify-center rounded-full text-app-muted hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <HiChevronRight className="h-5 w-5" />
        </button>

        {pickerOpen && (
          <MonthYearPicker
            viewYear={year}
            viewMonth={month}
            currentYear={currentYear}
            currentMonth={currentMonth}
            onSelect={(selectedYear, selectedMonth) => {
              onSelectMonth(selectedYear, selectedMonth);
              setPickerOpen(false);
            }}
          />
        )}
      </div>

      {/* Fixed 42px columns around 38px days, as on mobile — left to stretch, the grid spreads
          sideways and the rows read cramped by comparison. */}
      <div className="mx-auto mt-3 grid w-[294px] grid-cols-7 justify-items-center">
        {weekdays.map((weekday) => (
          <span key={weekday.id} className="text-center text-[11px] font-extrabold text-app-muted">
            {weekday.initial}
          </span>
        ))}
      </div>

      <div className="mx-auto mt-0.5 grid w-[294px] grid-cols-7 justify-items-center gap-y-[5px]">
        {days.map((day, index) => {
          const isSelected = day.date === selectedDay?.date;
          return (
            <button
              type="button"
              key={day.date}
              onClick={() => setOpenedDate(isSelected ? null : day.date)}
              // Day 1 starts under its real weekday; the rest follow it across the grid.
              style={index === 0 ? { gridColumnStart: leadingBlanks + 1 } : undefined}
              // Selected always wins over active, so a tapped day reads as one state either way.
              className={`flex h-[38px] w-[38px] items-center justify-center rounded-[5px] text-[10px] font-bold ${
                isSelected
                  ? "bg-app-ink text-white"
                  : day.hasActivity
                    ? "bg-app-blue text-white"
                    : "bg-[#DFDFDF] text-app-muted"
              }`}
            >
              {Number(day.date.slice(-2))}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <DayActivityDetail
          studentId={studentId}
          timezone={timezone}
          date={selectedDay.date}
          wasMarkedActive={selectedDay.hasActivity}
          onClose={() => setOpenedDate(null)}
        />
      )}
    </div>
  );
}
